/**
 * Retour du TPE sur la vente (réglage par commerce `retour_tpe_detaille`).
 *
 * - `CardDetails` : données complémentaires renvoyées par le terminal, stockées dans `sales.card_details`
 *   (jsonb). Les identifiants déjà présents en colonnes (card_scheme, pan_masked, auth_code, aid, entry_mode,
 *   tid, mid, stan, rrn) n'y sont pas répétés.
 * - Normalisation des saisies CB EXT (n° d'autorisation, 4 derniers chiffres).
 * - `buildCardReceiptLine` : ligne unique du ticket « CB : VISA ****1234 · Aut. 123456 · TID 80391519 ».
 *
 * PCI DSS : jamais de PAN complet, de piste, de cryptogramme ni de nom du porteur. Seuls les 4 derniers
 * chiffres sont gardés, sous la forme `****1234`.
 */
import { z } from 'zod';

export const CARD_DETAILS_SOURCES = ['mypos', 'tpe_externe', 'visionpos_tpe'] as const;

const shortText = (max: number) => z.string().trim().min(1).max(max).optional().catch(undefined);

export const cardDetailsSchema = z.object({
  /** Chemin d'encaissement : SDK myPOS, TPE bancaire externe (saisie), VisionPOS TPE / SoftPOS (Stripe). */
  source: z.enum(CARD_DETAILS_SOURCES),
  /** Réseau / marque quand card_scheme ne le porte pas (TPE externe, Stripe). */
  brand: shortText(30),
  /** Libellé de l'application carte (ex. « CB », « VISA DEBIT »). */
  application_label: shortText(40),
  /** Méthode de vérification du porteur (PIN, signature, sans). */
  cvm: shortText(30),
  /** Devise ISO 4217 du terminal. */
  currency: shortText(3),
  /** Date et heure de la transaction côté terminal (ISO 8601). */
  transaction_at: shortText(40),
  /** Référence de transaction du terminal (myPOS TranRef). */
  tran_ref: shortText(64),
  /** true quand n° d'autorisation ou 4 chiffres ont été saisis par la caissière. */
  saisie: z.boolean().optional().catch(undefined),
});

export type CardDetails = z.infer<typeof cardDetailsSchema>;

/** Retire les clés vides : la colonne ne garde que ce que le terminal a vraiment renvoyé. */
export function compactCardDetails(details: CardDetails): CardDetails {
  const parsed = cardDetailsSchema.parse(details);
  return Object.fromEntries(
    Object.entries(parsed).filter(([, v]) => v !== undefined && v !== ''),
  ) as CardDetails;
}

/** 4 derniers chiffres saisis : exactement 4 chiffres, sinon null. */
export function normalizeLast4(input: string | null | undefined): string | null {
  const digits = (input ?? '').replace(/\D/g, '');
  return digits.length === 4 ? digits : null;
}

/** N° d'autorisation saisi : 2 à 12 caractères alphanumériques, en majuscules, sinon null. */
export function normalizeAuthCode(input: string | null | undefined): string | null {
  const v = (input ?? '').replace(/\s/g, '').toUpperCase();
  return /^[A-Z0-9]{2,12}$/.test(v) ? v : null;
}

/** `****1234` à partir des 4 derniers chiffres. */
export function maskFromLast4(last4: string): string {
  return '****' + last4;
}

/** 4 derniers chiffres d'un PAN déjà masqué (« **** 1234 », « ************4242 »), sinon null. */
export function last4FromPanMasked(panMasked: string | null | undefined): string | null {
  const m = (panMasked ?? '').match(/(\d{4})\s*$/);
  return m ? m[1] : null;
}

/** Marqueur des encaissements sur TPE externe et VisionPOS TPE (cf. emvParams). */
const EXTERNAL_SCHEME = 'EXTERNAL_TPE';

export type CardReceiptInput = {
  card_scheme?: string | null;
  pan_masked?: string | null;
  auth_code?: string | null;
  tid?: string | null;
  card_details?: CardDetails | null;
};

/**
 * Ligne carte du ticket de caisse, ou null si la vente ne porte aucune donnée carte.
 * Ex. « CB : VISA ****1234 · Aut. 123456 · TID 80391519 », « CB : TPE externe ****1234 · Aut. 0A12B3 ».
 */
export function buildCardReceiptLine(input: CardReceiptInput): string | null {
  const scheme = input.card_scheme?.trim() || '';
  const brand = input.card_details?.brand?.trim();
  const last4 = last4FromPanMasked(input.pan_masked);
  // Reprise manuelle myPOS : le n° est stocké « MANUAL-RECOVER:<n°> », seul le n° va sur le ticket.
  const auth = input.auth_code?.trim().replace(/^MANUAL-RECOVER:/, '');
  const tid = input.tid?.trim();
  if (!scheme && !brand && !last4 && !auth && !tid) return null;

  let label: string;
  if (brand) label = brand.toUpperCase();
  else if (scheme === EXTERNAL_SCHEME) label = 'TPE externe';
  else label = scheme || 'Carte';

  const parts = [label + (last4 ? ' ' + maskFromLast4(last4) : '')];
  if (auth) parts.push('Aut. ' + auth);
  if (tid) parts.push('TID ' + tid);
  return 'CB : ' + parts.join(' · ');
}

/** Lit `sales.card_details` (jsonb) sans faire confiance à son contenu. */
export function parseCardDetails(raw: unknown): CardDetails | null {
  if (raw == null || typeof raw !== 'object') return null;
  const r = cardDetailsSchema.safeParse(raw);
  return r.success ? r.data : null;
}

/**
 * Ligne carte du ticket pour une vente lue en base : seulement si le commerce a activé retour_tpe_detaille
 * et n'a pas masqué la ligne dans le réglage du ticket (ticket_config.show_ligne_carte).
 */
export function cardLineFor(
  sale: Omit<CardReceiptInput, 'card_details'> & { card_details?: unknown },
  detailedCard: boolean,
  config: { show_ligne_carte?: boolean },
): string | undefined {
  if (!detailedCard || config.show_ligne_carte === false) return undefined;
  return (
    buildCardReceiptLine({
      card_scheme: sale.card_scheme,
      pan_masked: sale.pan_masked,
      auth_code: sale.auth_code,
      tid: sale.tid,
      card_details: parseCardDetails(sale.card_details),
    }) ?? undefined
  );
}
